import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from '../../utils/axiosInstanse';
import ToastNotification from '../Notification/ToastNotification';
import { clearNotification } from '../../reduser/notificationSlice';
import { showNotification } from '../../utils/notificationUtils';
import useIsMobile from '../../utils/useIsMobile';
import Spiner from "../Spiner/Spiner";
import BlacklistEditor from './BlacklistEditor';
import "./PassApplicationsList.css";
import host from "../../host";

import { TbReportAnalytics } from "react-icons/tb";
import ReportModal from './ReportModal';

const PassApplicationsList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState('');
  const [commentToUpdate, setCommentToUpdate] = useState('');
  const [activeFilter, setActiveFilter] = useState('new');
  const [hasAccess, setHasAccess] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // === Состояния для черного списка ===
  const [isBlacklistEditorOpen, setIsBlacklistEditorOpen] = useState(false);
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistMap, setBlacklistMap] = useState({});

  const dispatch = useDispatch();
  const notification = useSelector(state => state.notification);
  const isMobile = useIsMobile(1268);

  // === Функция загрузки черного списка ===
  const fetchBlacklist = async () => {
    try {
      const response = await axiosInstance.get(`${host}api/get_black_list`);
      const data = response.data;
      setBlacklist(data);
      const map = {};
      data.forEach(person => {
        const fioLower = person.fio ? person.fio.toLowerCase().trim() : '';
        if (fioLower) map[fioLower] = person;
      });
      setBlacklistMap(map);
    } catch (err) { /* Не блокируем работу */ }
  };

  // === Проверка наличия в черном списке (только полное совпадение ФИО) ===
  const checkBlacklist = (guestName) => {
    if (!guestName) return null;
    const nameLower = guestName.toLowerCase().trim();
    return blacklistMap[nameLower] || null;
  };

  // === Группировка заявок по ID с объединением гостей ===
  const groupApplicationsById = (apps) => {
    const grouped = {};
    apps.forEach(app => {
      const id = app.ID;
      if (!grouped[id]) {
        grouped[id] = {
          ...app,
          GUEST_ORGANIZATION: app.GUEST_ORGANIZATION,
          GUESTS: [{
            name: app.GUEST_NAME,
            comment: app.APPL_COMMENT || '',
          }],
        };
      } else {
        const exists = grouped[id].GUESTS.some(g => g.name === app.GUEST_NAME);
        if (!exists && app.GUEST_NAME) {
          grouped[id].GUESTS.push({
            name: app.GUEST_NAME,
            comment: app.APPL_COMMENT || '',
          });
        }
      }
    });
    return Object.values(grouped);
  };

  const fetchApplications = useCallback(async (pageNum, search = '', status = '') => {
    setLoading(true);
    try {
      const params = { page: pageNum };
      if (search && search.trim().length >= 3) {
        params.search = search.trim();
      }
      if (status) {
        params.status = status;
      }
      const response = await axiosInstance.get(`${host}api/v2/find_temporary_pass/`, {
        params,
      });
      const data = response.data.data || [];
      const sortedData = [...data].sort((a, b) => b.ID - a.ID);
      setApplications(sortedData);
      setTotalPages(response.data.total_pages || 1);
      setPage(response.data.page || pageNum);
      setHasAccess(true);
      await fetchBlacklist();
    } catch (err) {
      if (err.response?.status === 403) {
        setHasAccess(false);
      } else if (err.response) {
        showNotification(dispatch, `Ошибка сервера`, "error");
      } else if (err.request) {
        showNotification(dispatch, "Нет ответа от сервера. Проверьте подключение к интернету.", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const statusForFilter = activeFilter === 'new' ? 'empty' : activeFilter === 'rejected' ? 'DECLINED' : '';

  useEffect(() => {
    fetchApplications(1, '', statusForFilter);
  }, [fetchApplications, statusForFilter]);

  // Группируем и сортируем заявки по ID
  const groupedApplications = useMemo(() => {
    const grouped = groupApplicationsById(applications);
    return grouped.sort((a, b) => b.ID - a.ID);
  }, [applications]);

  const handleCloseNotification = () => { dispatch(clearNotification()); };

  useEffect(() => {
    if (notification.show) {
      const now = Date.now();
      const elapsedTime = now - notification.startTime;
      const remainingTime = 4000 - elapsedTime;
      if (remainingTime > 0) {
        const timer = setTimeout(() => { dispatch(clearNotification()); }, remainingTime);
        return () => clearTimeout(timer);
      } else { dispatch(clearNotification()); }
    }
  }, [notification, dispatch]);

  const handleRowClick = (app) => {
    setSelectedApplication(app);
    const isResolved = app.ORIGINAL_STATUS === 'APPROVED' || app.ORIGINAL_STATUS === 'DECLINED';
    setStatusToUpdate(isResolved ? '' : 'Требует согласования');
    setCommentToUpdate(app.APPL_COMMENT || '');
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setSelectedApplication(null); };
  const closeReportModal = () => { setIsReportModalOpen(false); };
  const handleStatusChange = (e) => { setStatusToUpdate(e.target.value); };
  const handleCommentChange = (e) => { setCommentToUpdate(e.target.value); };

  const handleSaveStatus = async () => {
    if (!selectedApplication) return;
    const statusToCode = {
      'Согласована': 'APPROVED',
      'Отклонена': 'DECLINED',
      'Требует согласования': 'PENDING',
    };
    const statusToSend = statusToCode[statusToUpdate] || statusToUpdate;
    try {
      const response = await axiosInstance.patch(
        `${host}api/v2/temporary_pass_solution/${selectedApplication.ID}/`,
        { STATUS: statusToSend, COMMENT: commentToUpdate }
      );
      if (response.status === 200) {
        setApplications((prev) =>
          prev.map((app) =>
            app.ID === selectedApplication.ID
              ? { ...app, STATUS: statusToUpdate, ORIGINAL_STATUS: statusToSend, APPL_COMMENT: commentToUpdate }
              : app
          )
        );
        showNotification(dispatch, 'Данные успешно обновлены', 'success');
        closeModal();
      }
    } catch (err) {
      showNotification(dispatch, 'Ошибка при обновлении данных', 'error');
    }
  };

  const handleSearchChange = (e) => { setSearchQuery(e.target.value); };

  const handleSearchSubmit = () => {
    const query = searchQuery.trim();
    if (query.length >= 3) {
      fetchApplications(1, query, statusForFilter);
    } else if (query.length === 0) {
      fetchApplications(1, '', statusForFilter);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit' });
  };

  const getStatusOptions = () => [
    { value: 'Требует согласования', label: 'Требует согласования' },
    { value: 'Согласована', label: 'Согласована' },
    { value: 'Отклонена', label: 'Отклонена' },
  ];

  const getBadgeClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'pal-badge--approved';
      case 'DECLINED': return 'pal-badge--rejected';
      case '': return 'pal-badge--pending';
      default: return 'pal-badge--default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'APPROVED': return 'Одобрено';
      case 'DECLINED': return 'Отклонено';
      case '': return 'В процессе';
      default: return status || 'Не определено';
    }
  };

  // Рендер для мобильных устройств
  const renderMobileView = () => (
    <div className="pal-mobile-list">
      {groupedApplications.map((app) => {
        const anyInBlacklist = app.GUESTS?.some(g => checkBlacklist(g.name));
        return (
          <motion.div
            key={app.ID}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`pal-card ${anyInBlacklist ? 'pal-card--blacklist' : ''}`}
            onClick={() => handleRowClick(app)}
          >
            <div className="pal-card-header">
              <div>
                <div className="pal-card-name">
                  {app.GUESTS?.length > 1
                    ? `${app.GUESTS[0].name} +${app.GUESTS.length - 1}`
                    : app.GUEST_NAME || '—'
                  }
                </div>
                <div className="pal-card-id">ID: {app.ID}</div>
              </div>
              <span className={`pal-badge ${getBadgeClass(app.ORIGINAL_STATUS)}`}>
                {getStatusText(app.ORIGINAL_STATUS)}
              </span>
            </div>

            {anyInBlacklist && (
              <div className="pal-card-blacklist-banner">
                ⚠️ ЕСТЬ В ЧЁРНОМ СПИСКЕ
              </div>
            )}

            {app.GUEST_ORGANIZATION && (
              <div className="pal-card-org">
                <strong>Организация:</strong> {app.GUEST_ORGANIZATION}
              </div>
            )}

            {app.GUESTS?.length > 1 && (
              <div className="pal-card-guests">
                <div className="pal-card-guests-title">Гости ({app.GUESTS.length}):</div>
                <ul>
                  {app.GUESTS.map((g, idx) => (
                    <li key={idx}>
                      {g.name}
                      {g.comment && <span className="pal-guest-sub"> — <em>{g.comment}</em></span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pal-card-details">
              <div><strong>Цель:</strong> {app.VISIT_PURPOSE || '—'}</div>
              <div><strong>Место:</strong> {app.VISIT_PLACE_NAME || '—'}</div>
              <div><strong>К кому:</strong> {app.COMETO_DESCRIPTION || '—'}</div>
              <div><strong>Период:</strong> {formatDateShort(app.PERIOD_BEGIN)} — {formatDateShort(app.PERIOD_END)}</div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  // Рендер для десктопа
  const renderDesktopView = () => (
    <div className="pal-table-wrap">
      <div className="pal-table-scroll">
        <table className="pal-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>ФИО гостя</th>
              <th>Организация</th>
              <th>Цель визита</th>
              <th>Место</th>
              <th>К кому</th>
              <th>Статус</th>
              <th>Период</th>
            </tr>
          </thead>
          <tbody>
            {groupedApplications.map((app) => {
              const anyInBlacklist = app.GUESTS?.some(g => checkBlacklist(g.name));
              return (
                <tr
                  key={app.ID}
                  onClick={() => handleRowClick(app)}
                  className={`${anyInBlacklist ? 'pal-row--blacklist' : ''}`}
                >
                  <td>{app.ID}</td>
                  <td>
                    <div>{app.GUESTS?.[0]?.name || app.GUEST_NAME || '—'}</div>
                    {app.GUESTS?.length > 1 && (
                      <div className="pal-guest-more">+{app.GUESTS.length - 1} ещё</div>
                    )}
                    {anyInBlacklist && (
                      <div className="pal-blacklist-warning">⚠️ В ЧС</div>
                    )}
                  </td>
                  <td>{app.GUEST_ORGANIZATION || '—'}</td>
                  <td>{app.VISIT_PURPOSE || '—'}</td>
                  <td>{app.VISIT_PLACE_NAME || '—'}</td>
                  <td>{app.COMETO_DESCRIPTION || '—'}</td>
                  <td>
                    <span className={`pal-badge ${getBadgeClass(app.ORIGINAL_STATUS)}`}>
                      {getStatusText(app.ORIGINAL_STATUS)}
                    </span>
                  </td>
                  <td>
                    <div>{formatDate(app.PERIOD_BEGIN)}</div>
                    <div>{formatDate(app.PERIOD_END)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (hasAccess === false) {
    return (
      <div className="pal-container">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="pal-access-denied">
            У вас нет прав для доступа к данному разделу
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pal-container" style={{ paddingTop: 20 }}>
        <Spiner />
      </div>
    );
  }

  return (
    <div className="pal-container">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>

        {/* Панель фильтров и поиска */}
        <div className="pal-controls">
          <div className="pal-search-row">
            <div className="pal-search-group">
              <label>Поиск по ФИО гостя</label>
              <input
                type="text"
                className="pal-search-input"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                placeholder="Введите ФИО или часть имени..."
              />
            </div>
            <button
              className="pal-btn pal-btn--outline pal-btn--sm"
              onClick={handleSearchSubmit}
              style={{ marginBottom: 0, height: '43px' }}
            >
              🔍 Поиск
            </button>
            {searchQuery && (
              <button
                className="pal-btn pal-btn--outline pal-btn--sm"
                onClick={() => { setSearchQuery(''); fetchApplications(1, '', statusForFilter); }}
                style={{ marginBottom: 0, height: '43px' }}
              >
                ✕ Очистить
              </button>
            )}
          </div>

          <div className="pal-filters">
            <button
              className={`pal-filter-btn ${activeFilter === 'new' ? 'active' : ''}`}
              onClick={() => setActiveFilter('new')}
            >
              Новые
            </button>
            <button
              className={`pal-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              Все заявки
            </button>
            <button
              className={`pal-filter-btn pal-filter-btn--danger ${activeFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setActiveFilter('rejected')}
            >
              Отклонённые
            </button>
            <button
              className="pal-filter-btn pal-filter-btn--danger"
              onClick={() => setIsBlacklistEditorOpen(true)}
              title="Редактор черного списка"
            >
              🚫 Черный список
            </button>

            <button
              className="pal-filter-btn pal-filter-btn--success"
              onClick={() => setIsReportModalOpen(true)}
              title="Отчеты"
            >
              <div className="d-flex gap-1 align-items-center">
                <TbReportAnalytics size={20} color={"green"} /> Отчеты
              </div>
            </button>
          </div>
        </div>

        {groupedApplications.length === 0 ? (
          <div className="pal-empty">
            <p>Заявки не найдены</p>
          </div>
        ) : (
          <div>
            <div className="pal-result-count">Найдено: {groupedApplications.length} заявок</div>
            {isMobile ? renderMobileView() : renderDesktopView()}
            {totalPages > 1 && (
              <div className="pal-pagination">
                <button
                  className="pal-pagination-btn"
                  disabled={page <= 1}
                  onClick={() => fetchApplications(page - 1, searchQuery, statusForFilter)}
                >
                  ←
                </button>
                <span className="pal-pagination-info">
                  Стр. {page} из {totalPages}
                </span>
                <button
                  className="pal-pagination-btn"
                  disabled={page >= totalPages}
                  onClick={() => fetchApplications(page + 1, searchQuery, statusForFilter)}
                >
                  →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Модальное окно редактирования */}
        {isModalOpen && selectedApplication && (
          <div className="pal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
            <div className="pal-modal">
              <div className="pal-modal-header">
                <h2 className="pal-modal-title">Заявка #{selectedApplication.ID}</h2>
                <button className="pal-modal-close" onClick={closeModal}>✕</button>
              </div>

              <div className="pal-modal-body">
                {/* Проверка черного списка */}
                {(() => {
                  const guestsToCheck = selectedApplication.GUESTS || [{ name: selectedApplication.GUEST_NAME }];
                  const blacklistedGuests = guestsToCheck.map(g => ({ ...g, info: checkBlacklist(g.name) })).filter(g => g.info);
                  if (blacklistedGuests.length > 0) {
                    return (
                      <div className="pal-alert-danger">
                        <h3>⚠️ ВНИМАНИЕ!</h3>
                        <p>{blacklistedGuests.length} гость(ей) в ЧЁРНОМ СПИСКЕ:</p>
                        <div style={{ textAlign: 'left' }}>
                          {blacklistedGuests.map((g, idx) => (
                            <div key={idx} className="pal-alert-item">
                              <strong>{g.name}</strong>
                              {g.info?.description && <div className="reason">Причина: {g.info.description}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {(selectedApplication.ORIGINAL_STATUS === '' || selectedApplication.ORIGINAL_STATUS == null) ? (
                  <>
                    <div className="pal-grid-2">
                      <div className="pal-form-group">
                        <label>Статус</label>
                        <select className="pal-form-select" value={statusToUpdate} onChange={handleStatusChange}>
                          {getStatusOptions().map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="pal-form-group">
                      <label>Комментарий</label>
                      <textarea
                        className="pal-form-textarea"
                        value={commentToUpdate}
                        onChange={handleCommentChange}
                        placeholder="Введите комментарий..."
                        rows={3}
                      />
                    </div>
                  </>
                ) : (
                  <div className="pal-form-group">
                    <label>Статус</label>
                    <div>
                      <span className={`pal-badge ${getBadgeClass(selectedApplication.ORIGINAL_STATUS)}`}>
                        {getStatusText(selectedApplication.ORIGINAL_STATUS)}
                      </span>
                    </div>
                  </div>
                )}

                {(selectedApplication.safetyBriefing === "1" || selectedApplication.weekendVisit === "1" || selectedApplication.vehicleAccess === "1") && (
                  <div className="pal-params-block">
                    <h6>Дополнительные требования:</h6>
                    <div className="pal-grid-3">
                      {selectedApplication.safetyBriefing === "1" && (
                        <div className="pal-param-tag pal-param-tag--success">
                          <span>🛡️</span> <strong>Инструктаж по ОТ и ТБ</strong>
                        </div>
                      )}
                      {selectedApplication.weekendVisit === "1" && (
                        <div className="pal-param-tag pal-param-tag--warning">
                          <span>📅</span> <strong>Визит в выходные/праздники</strong>
                        </div>
                      )}
                      {selectedApplication.vehicleAccess === "1" && (
                        <div className="pal-param-tag pal-param-tag--info">
                          <span>🚗</span> <strong>Въезд на авто</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedApplication.vehicleAccess === "1" && (
                  <div className="pal-vehicle-block">
                    <h6>🚛 Данные об автомобиле</h6>
                    {(() => {
                      const vehicles = selectedApplication.cars;
                      if (Array.isArray(vehicles) && vehicles.length > 0) {
                        return (
                          <div className="pal-vehicles-list">
                            {vehicles.map((v, idx) => (
                              <div key={v.ID ?? idx} className="pal-vehicle-item">
                                <div className="pal-vehicle-item-title">
                                  Машина {idx + 1}
                                </div>
                                <div className="pal-grid-2">
                                  <div>
                                    <div className="pal-form-label-muted">Госномер:</div>
                                    <div className="pal-form-value">{v.LPNUMBER || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="pal-form-label-muted">Водитель:</div>
                                    <div className="pal-form-value">{v.DRIVER || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="pal-form-label-muted">Марка:</div>
                                    <div className="pal-form-value">{v.MODEL || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="pal-form-label-muted">Цвет:</div>
                                    <div className="pal-form-value">{v.COLOR || '—'}</div>
                                  </div>
                                  {v.COMMENT && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <div className="pal-form-label-muted">Комментарий:</div>
                                      <div className="pal-form-value">{v.COMMENT}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <div className="pal-grid-2">
                          <div><div className="pal-form-label-muted">Госномер:</div><div className="pal-form-value">{selectedApplication.CAR_LPNUMBER || '—'}</div></div>
                          <div><div className="pal-form-label-muted">Водитель:</div><div className="pal-form-value">{selectedApplication.CAR_DRIVER || '—'}</div></div>
                          <div><div className="pal-form-label-muted">Марка:</div><div className="pal-form-value">{selectedApplication.CAR_MODEL || '—'}</div></div>
                          <div><div className="pal-form-label-muted">Цвет:</div><div className="pal-form-value">{selectedApplication.CAR_COLOR || '—'}</div></div>
                          <div style={{ gridColumn: '1 / -1' }}><div className="pal-form-label-muted">Комментарий:</div><div className="pal-form-value">{selectedApplication.CAR_COMMENT || '—'}</div></div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <div className="pal-form-label-muted">Организация</div>
                  <div className="pal-form-value">{selectedApplication.GUEST_ORGANIZATION || '—'}</div>
                </div>

                <div className="pal-form-group">
                  <div className="pal-form-label-muted" style={{ marginBottom: '6px', fontWeight: 600 }}>
                    {selectedApplication.GUESTS?.length > 1 ? 'Список гостей:' : 'ФИО гостя:'}
                  </div>
                  {selectedApplication.GUESTS?.length > 1 ? (
                    <div className="pal-guest-list">
                      {selectedApplication.GUESTS.map((g, idx) => (
                        <div key={idx} className="pal-guest-item">
                          <div className="pal-guest-item-name">{idx + 1}. {g.name}</div>
                          <div className="pal-guest-item-comment">
                            <strong>Комментарий:</strong> {g.comment}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <div className="pal-form-value">{selectedApplication.GUEST_NAME || '—'}</div>
                      {selectedApplication.APPL_COMMENT && (
                        <div className="pal-guest-item-comment" style={{ marginTop: 4 }}>Комментарий: {selectedApplication.APPL_COMMENT}</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="pal-grid-2">
                  <div className="pal-form-group">
                    <div className="pal-form-label-muted">Цель визита</div>
                    <div className="pal-form-text">{selectedApplication.VISIT_PURPOSE || '—'}</div>
                  </div>
                  <div className="pal-form-group">
                    <div className="pal-form-label-muted">Место посещения</div>
                    <div className="pal-form-text">{selectedApplication.VISIT_PLACE_NAME || '—'}</div>
                  </div>
                </div>

                <div className="pal-form-group">
                  <div className="pal-form-label-muted">Инициатор визита</div>
                  <div className="pal-form-text">{selectedApplication.COMETO_DESCRIPTION || '—'}</div>
                  <div className="pal-guest-item-comment" style={{ marginTop: 2 }}>
                    Табельный: <strong>{selectedApplication.COMETO_NAME || '—'}</strong>
                  </div>
                </div>

                <div className="pal-grid-2">
                  <div className="pal-form-group">
                    <div className="pal-form-label-muted">Период</div>
                    <div className="pal-form-text">{formatDate(selectedApplication.PERIOD_BEGIN)} — {formatDate(selectedApplication.PERIOD_END)}</div>
                  </div>
                  <div className="pal-form-group">
                    <div className="pal-form-label-muted">Создано</div>
                    <div className="pal-form-text">{formatDate(selectedApplication.CREATEDTIME)}</div>
                  </div>
                </div>
              </div>

              <div className="pal-modal-footer">
                <button className="pal-btn pal-btn--secondary" onClick={closeModal}>Закрыть</button>
                {(selectedApplication.ORIGINAL_STATUS === '' || selectedApplication.ORIGINAL_STATUS == null) && (
                  <button className="pal-btn pal-btn--primary" onClick={handleSaveStatus}>Сохранить</button>
                )}
              </div>
            </div>
          </div>
        )}

        {isReportModalOpen && (
          <ReportModal isOpen={isReportModalOpen} closeReportModal={closeReportModal} showNotification={showNotification} isMobile={isMobile} />
        )}

        <BlacklistEditor isOpen={isBlacklistEditorOpen} onClose={() => setIsBlacklistEditorOpen(false)} />

        {notification.show && (
          <ToastNotification
            show={notification.show}
            message={notification.message}
            onClose={handleCloseNotification}
            type={notification.type}
          />
        )}
      </motion.div>
    </div>
  );
};

export default PassApplicationsList;